import { MAX_RECORDING_SECONDS, MAX_UPLOAD_BYTES } from '@feedback/shared';

/**
 * Screen recording, stopped by whichever limit arrives first.
 *
 * Three things can end a recording and all three are ordinary: the reporter
 * presses Stop, the 30-second budget runs out, or the accumulated bytes reach
 * the 10 MB upload ceiling (ADR 0030). A fourth ends it from outside the
 * widget entirely: every browser puts its own "Stop sharing" control on a
 * screen capture, and pressing that ends the video track. That one is not an
 * error and must not be reported as one, because it is how a good number of
 * people will stop.
 *
 * **Both caps are enforced HERE rather than at upload.** A reporter who
 * records for two minutes and is only then told the file is too large has done
 * the work twice, and the second attempt is the one where they give up.
 *
 * Deliberately no module-level reference to `MediaRecorder` or
 * `navigator.mediaDevices`. jsdom has neither, and this module is imported by
 * `Modal`, so touching them at import time would break every unit test in the
 * package for a feature none of them exercise. `isRecordingSupported()` is the
 * only thing a caller needs before offering the control.
 */

/** What the caller gets back when a recording ends normally. */
export interface RecordingResult {
  blob: Blob;
  /** Whole seconds, for the label on the attachment chip. */
  durationSeconds: number;
  /** True when a cap ended it rather than the reporter, so the UI can say which. */
  stoppedBy: 'user' | 'duration' | 'size' | 'source';
}

export interface RecordingSession {
  /** Ends the recording and resolves the result. Safe to call twice. */
  stop(): Promise<RecordingResult>;
  /** Elapsed whole seconds, for the meter. */
  elapsedSeconds(): number;
  /** Abandons the recording and releases the camera, screen and microphone. */
  cancel(): void;
}

export interface ScreenRecorder {
  start(): Promise<RecordingSession>;
}

/**
 * Whether the browser can record at all.
 *
 * Checked rather than assumed, because the control must not be offered where
 * pressing it can only fail: `getDisplayMedia` is absent in every non-secure
 * context and in several mobile browsers, and the widget is embedded on pages
 * this product has never seen.
 */
export function isRecordingSupported(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function' &&
    typeof MediaRecorder !== 'undefined'
  );
}

/**
 * The first container this browser will actually record, or undefined to let it choose.
 *
 * Asked in preference order rather than hardcoded: Chromium records VP9 and
 * Firefox does not, and passing a `mimeType` a browser rejects throws
 * `NotSupportedError` from the constructor rather than degrading.
 */
function preferredMimeType(): string | undefined {
  const candidates = ['video/webm;codecs=vp9,opus', 'video/webm;codecs=vp8,opus', 'video/webm'];
  return candidates.find((type) => MediaRecorder.isTypeSupported(type));
}

/**
 * Adds the microphone to a display stream, when the reporter allows it.
 *
 * Best effort on purpose. A denied microphone is a normal answer and must not
 * cost the reporter the recording they did agree to; a screen capture with no
 * narration is still the thing they set out to send.
 *
 * The two audio sources are mixed through one `AudioContext` destination rather
 * than both being added to the track list, because `MediaRecorder` records the
 * FIRST audio track and silently ignores the rest, so a naive two-track stream
 * drops whichever the browser happened to order second.
 */
async function withMicrophone(display: MediaStream): Promise<{ stream: MediaStream; cleanup: () => void }> {
  let mic: MediaStream | undefined;
  try {
    mic = await navigator.mediaDevices.getUserMedia({ audio: true });
  } catch {
    return { stream: display, cleanup: () => undefined };
  }

  const displayAudio = display.getAudioTracks();
  if (displayAudio.length === 0) {
    // Nothing to mix with: hand the microphone track over directly.
    const combined = new MediaStream([...display.getVideoTracks(), ...mic.getAudioTracks()]);
    return { stream: combined, cleanup: () => mic?.getTracks().forEach((t) => t.stop()) };
  }

  const context = new AudioContext();
  const destination = context.createMediaStreamDestination();
  context.createMediaStreamSource(new MediaStream(displayAudio)).connect(destination);
  context.createMediaStreamSource(mic).connect(destination);

  const combined = new MediaStream([...display.getVideoTracks(), ...destination.stream.getAudioTracks()]);
  return {
    stream: combined,
    cleanup: () => {
      mic?.getTracks().forEach((track) => track.stop());
      void context.close();
    },
  };
}

/**
 * The real recorder. Constructed by the controller; never at import time.
 *
 * `start()` raises the browser's own screen-share picker, which is why the UI
 * asks first: a permission dialog nobody expected is how a reporter learns to
 * dismiss them.
 */
export class MediaStreamRecorder implements ScreenRecorder {
  async start(): Promise<RecordingSession> {
    const display = await navigator.mediaDevices.getDisplayMedia({
      video: true,
      // Asked for, not required. A browser that refuses system audio still
      // records the screen, and `withMicrophone` covers the narration case.
      audio: true,
    });

    const { stream, cleanup } = await withMicrophone(display);
    const mimeType = preferredMimeType();
    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);

    const chunks: Blob[] = [];
    let bytes = 0;
    let stoppedBy: RecordingResult['stoppedBy'] = 'user';
    const startedAt = Date.now();
    let settled: ((result: RecordingResult) => void) | undefined;

    // Declared with `let` ABOVE the releaser that clears it. `releaseEverything`
    // runs from the 'stop' and 'ended' handlers, both registered before the
    // timer is armed, so a `const` below them is a temporal-dead-zone throw
    // waiting for the first browser that fires either synchronously.
    let durationTimer: number | undefined;

    const releaseEverything = () => {
      display.getTracks().forEach((track) => track.stop());
      stream.getTracks().forEach((track) => track.stop());
      cleanup();
      window.clearTimeout(durationTimer);
    };

    const finished = new Promise<RecordingResult>((resolve) => {
      settled = resolve;
    });

    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size === 0) return;
      chunks.push(event.data);
      bytes += event.data.size;
      // The size cap is checked per chunk rather than at the end, so a long
      // recording stops at the ceiling instead of being refused after the fact.
      if (bytes >= MAX_UPLOAD_BYTES && recorder.state === 'recording') {
        stoppedBy = 'size';
        recorder.stop();
      }
    });

    recorder.addEventListener('stop', () => {
      releaseEverything();
      settled?.({
        blob: new Blob(chunks, { type: mimeType?.split(';')[0] ?? 'video/webm' }),
        durationSeconds: Math.round((Date.now() - startedAt) / 1000),
        stoppedBy,
      });
    });

    // The browser's own "Stop sharing" button ends the track without touching
    // the recorder, so without this the recording runs on capturing a dead
    // stream until the duration cap.
    display.getVideoTracks().forEach((track) => {
      track.addEventListener('ended', () => {
        if (recorder.state === 'recording') {
          stoppedBy = 'source';
          recorder.stop();
        }
      });
    });

    durationTimer = window.setTimeout(() => {
      if (recorder.state === 'recording') {
        stoppedBy = 'duration';
        recorder.stop();
      }
    }, MAX_RECORDING_SECONDS * 1000);

    // One chunk per second, so the size cap is checked about as often as it
    // could plausibly be crossed. Recording into a single chunk would mean the
    // cap is only ever known once the recording is already over.
    recorder.start(1000);

    return {
      stop: () => {
        if (recorder.state === 'recording') recorder.stop();
        return finished;
      },
      elapsedSeconds: () => Math.floor((Date.now() - startedAt) / 1000),
      cancel: () => {
        // Ordered so `stop` cannot resolve a result nobody will use: release
        // first, then stop, and the `stop` handler still fires and settles the
        // promise for any caller that already held it.
        if (recorder.state === 'recording') recorder.stop();
        releaseEverything();
      },
    };
  }
}
